import { useLayoutEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { enforceMinimumWidgetWidth } from 'redux/modules/superreports.types';
import type { ThunkDispatch } from 'types/redux';
import { ViewportWidthBreakpoint } from 'utils/viewport';

const getParentOverflow = <T extends Element>(element: T, width: number) => {
  const parent = element.parentElement;
  return parent?.clientWidth && parent.clientWidth < width ? width : 0;
};

/**
 * Watches the width of a widget element to trigger an event if the widget overflows it container.
 * If it does, we need to lock the minimum width of the report to prevent content from clipping.
 */
export const useWidgetResizeObserver = <T extends Element = Element>(
  element: T | null,
  currentWidthBreakpoint: ViewportWidthBreakpoint
) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const minWidth = useRef(0);

  const resizeObserver = useRef(
    new ResizeObserver(entries => {
      const nextMinWidth = entries.reduce((tempMinWidth, entry) => {
        if (!entry.borderBoxSize) return tempMinWidth;

        // There are still some browser differences in the implementation:
        // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
        const borderBoxSize = entry.borderBoxSize[0] ?? entry.borderBoxSize;

        const overflowWidth = getParentOverflow(entry.target, Math.ceil(borderBoxSize.inlineSize));
        return overflowWidth > 0 ? Math.max(overflowWidth, tempMinWidth) : tempMinWidth;
      }, 0);

      if (nextMinWidth > 0 && nextMinWidth !== minWidth.current) {
        minWidth.current = nextMinWidth;
        dispatch(enforceMinimumWidgetWidth(nextMinWidth));
      }
    })
  );

  useLayoutEffect(() => {
    if (!element || currentWidthBreakpoint < ViewportWidthBreakpoint.Medium) return;

    const observer = resizeObserver.current;
    observer.observe(element, { box: 'border-box' });
    return () => {
      observer.disconnect();
    };
  }, [element, currentWidthBreakpoint]);
};
